/**
 * useSidebarBadges Hook
 * Fetches task counts for sidebar badge display
 * Polls every 60 seconds for updates
 */

import { useState, useEffect, useCallback } from 'react'
import { authFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'

interface TaskCounts {
  urgent: number
  approaching: number
  other: number
  total: number
}

interface SidebarBadges {
  taskCount: number
  hasUrgent: boolean
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
  timestamp: string
}

interface DashboardSummary {
  tasks: {
    counts: TaskCounts
  }
}

const POLL_INTERVAL = 60000 // 60 seconds

export function useSidebarBadges(): SidebarBadges {
  const [taskCount, setTaskCount] = useState(0)
  const [hasUrgent, setHasUrgent] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCounts = useCallback(async () => {
    const token = useAuthStore.getState().tokens?.accessToken
    if (!token) {
      setIsLoading(false)
      return
    }

    try {
      const response = await authFetch<ApiResponse<DashboardSummary>>(
        '/api/v1/dashboard/summary',
        token
      )

      if (response.success && response.data?.tasks?.counts) {
        const { counts } = response.data.tasks
        setTaskCount(counts.total)
        setHasUrgent(counts.urgent > 0)
        setError(null)
      }
    } catch (err) {
      // Silently fail - badge is non-critical
      console.error('Failed to fetch sidebar badges:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch counts')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    fetchCounts()
  }, [fetchCounts])

  // Poll for updates
  useEffect(() => {
    const interval = setInterval(fetchCounts, POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchCounts])

  return {
    taskCount,
    hasUrgent,
    isLoading,
    error,
    refresh: fetchCounts,
  }
}
